import type { DesignSystemIcons } from "design-system/types";
import { storeToRefs } from "pinia";
import { computed, inject } from "vue";
import type { RouteLocationRaw } from "vue-router";

import { track } from "shared/boot/analytics";
import {
  crmFeatures,
  outreachFeatures,
  socialFeatures,
  teaserFeatures,
} from "shared/boot/authorization";
import { getLocaleText } from "shared/boot/i18n";
import ModalService from "shared/modals/ModalService";
import type { AppConfigMenuLogo } from "shared/services/appConfig/types";
import features from "shared/services/features";
import { useGlobalStore } from "shared/stores/global";
import { useStreamGroupsStore } from "shared/stores/streamGroups";
import { useStreamsStore } from "shared/stores/streams";
import { useUserStore } from "shared/stores/user";
import type { Nullable, Stream, StreamGroup } from "shared/types";
import { type FeatureKey } from "shared/types/features";

import useFeatureTeasers from "./useFeatureTeasers";
import useProductRouting from "./useProductRouting";

export type ProductMenuKey =
  | "competitor"
  | "crm"
  | "csl"
  | "dashboard"
  | "insights"
  | "media"
  | "outreach"
  | "social";

export interface ProductMenuItem {
  ariaLabel: string;
  hasTeaser: boolean;
  hasViewedTeaser?: boolean;
  icon?: DesignSystemIcons;
  iconImg?: string;
  isActive: boolean;
  isEnabled: boolean;
  isVisible: boolean;
  key: ProductMenuKey;
  label: string;
  route: RouteLocationRaw;
  testId: string;
  tooltip: string;
}

export default function useProductMenu(isDesktopApp: boolean = false) {
  const isDesktop = inject<boolean>("isDesktop", false);
  const isMobile = inject<boolean>("isMobile", false);

  const userStore = useUserStore();
  const { hasLaunchpadBetaEnabled } = storeToRefs(userStore);
  const { appConfig } = storeToRefs(useGlobalStore());

  const streamsStore = useStreamsStore();
  const streamGroupsStore = useStreamGroupsStore();

  const { routeProductName } = useProductRouting();

  const { createViewedItemForFeature, viewedFeatureTeaser } =
    useFeatureTeasers();

  const mediaProductMenuEnabled = computed<boolean>(
    () => features.has("has_media_platform") || isMobile === true
  );

  const socialProductMenuEnabled = computed<boolean>(() =>
    socialFeatures.canViewSocialPlatform()
  );

  const hasSocialAnalyse = computed<boolean>(() =>
    socialFeatures.canViewSocialAnalyse()
  );

  const canAccessInstantInsights = computed<boolean>(() =>
    features.has("has_instant_insights")
  );

  const crmMenuEnabled = computed<boolean>(() =>
    crmFeatures.canViewCrmPlatform()
  );

  const outreachProductMenuEnabled = computed<boolean>(() =>
    outreachFeatures.canViewOutreachPlatform()
  );

  const socialProductTeaserEnabled = computed<boolean>(() =>
    teaserFeatures.canViewSocialPlatformTeaser()
  );

  const outreachProductTeaserEnabled = computed<boolean>(() =>
    teaserFeatures.canViewOutreachPlatformTeaser()
  );

  const insightsProductMenuEnabled = computed<boolean>(
    () =>
      isDesktop === true &&
      (canAccessInstantInsights.value || hasSocialAnalyse.value)
  );

  const cslProductMenuEnabled = computed<boolean>(
    () => features.has("has_csl") && isDesktop === true
  );

  function firstSocialGroupWithStreams(): Nullable<StreamGroup> {
    const filteredGroups = streamGroupsStore
      .socialStreamGroups()
      .filter((group) => group.streams.length);

    return filteredGroups.length ? filteredGroups[0] : null;
  }

  function displayPrimaryStream(stream: Stream): boolean {
    return !stream.primary || !stream.dashboard_only;
  }

  function socialStreamsByGroupId(groupId: number): Stream[] {
    return streamsStore
      .socialStreams()
      .filter(
        (stream) => displayPrimaryStream(stream) && stream.group_id === groupId
      );
  }

  const mediaRoute = computed<RouteLocationRaw>(() => ({ name: "launchpad" }));

  const socialRoute = computed<RouteLocationRaw>(() => {
    const group = firstSocialGroupWithStreams();
    const streams = group ? socialStreamsByGroupId(group.id) : [];

    let routeDetails: RouteLocationRaw = {};

    const isDesktopRoute = isDesktopApp || isDesktop;

    if (socialFeatures.canViewSocialListen()) {
      if (!streams.length || !group) {
        routeDetails = isDesktopRoute
          ? { name: "social-monitor" }
          : { name: "social" };
      } else {
        routeDetails = {
          name: "social-monitor",
          params: {
            groupSlug: group.slug,
            streamSlug: streams[0].slug,
          },
        };
      }
    } else if (socialFeatures.canViewSocialEngage()) {
      routeDetails = isDesktopRoute ? { name: "social-engage" } : {};
    }

    return routeDetails;
  });

  const cslRoute = computed<RouteLocationRaw>(() => ({ name: "csl" }));

  const competitorRoute = computed<RouteLocationRaw>(() => ({
    name: "competitor-dashboard",
  }));

  const crmRoute = computed<RouteLocationRaw>(() => ({
    name: "crm-activities",
  }));

  const insightsRoute = computed<RouteLocationRaw>(() => ({
    name: "insights",
  }));

  const outreachRoute = computed<RouteLocationRaw>(() => {
    if (!outreachProductMenuEnabled.value) {
      return { name: "launchpad" };
    }

    if (!hasLaunchpadBetaEnabled.value) {
      return { name: "outreach-launchpad" };
    }

    if (features.has("has_outreach_discover")) {
      return { name: "outreach-discover" };
    }

    return { name: "outreach-search" };
  });

  const currentRouteProductMenu = computed(() => routeProductName());

  const hasHelpMenu = computed<boolean>(() => features.has("has_help_menu"));

  const canViewSearchButton = computed<boolean>(
    () =>
      isMobile &&
      currentRouteProductMenu.value === "media" &&
      features.has("has_search")
  );

  const logoConfig = computed<AppConfigMenuLogo>(() => {
    switch (currentRouteProductMenu.value) {
      case "insights":
        return appConfig.value.logo.menu.insights;
      case "outreach":
        return (
          appConfig.value.logo.menu.outreach ?? appConfig.value.logo.menu.media
        );
      case "social":
        return appConfig.value.logo.menu.social;
      default:
        return appConfig.value.logo.menu.media;
    }
  });

  const isDarkMode = computed<boolean>(() =>
    Boolean(logoConfig.value.isDarkMode)
  );

  const menuLogoAlt = computed<string>(() => {
    const platformName =
      currentRouteProductMenu.value === "social"
        ? getLocaleText("social_product_name", {
            platformName: appConfig.value.platformName,
          })
        : appConfig.value.platformName;

    return getLocaleText("default_layout.platform_logo", { platformName });
  });

  const launchpadDashboardRoute = computed<RouteLocationRaw>(() => ({
    name: "dashboard",
  }));

  const productMenuItems = computed<ProductMenuItem[]>(() => {
    const items: ProductMenuItem[] = [
      {
        ariaLabel: getLocaleText("launchpad_dashboard_product_name"),
        hasTeaser: false,
        icon: "launchpad",
        isActive: currentRouteProductMenu.value === "dashboard",
        isEnabled: true,
        isVisible: hasLaunchpadBetaEnabled.value && isDesktop === true,
        key: "dashboard",
        label: getLocaleText("launchpad_dashboard_product_name"),
        route: launchpadDashboardRoute.value,
        testId: "dashboard-product-menu",
        tooltip: getLocaleText("launchpad_dashboard_product_name"),
      },
      {
        ariaLabel: getLocaleText("monitoring_product_name"),
        hasTeaser: false,
        icon: "mention-streams",
        isActive: currentRouteProductMenu.value === "media",
        isEnabled: true,
        isVisible: mediaProductMenuEnabled.value,
        key: "media",
        label: getLocaleText("monitoring_product_name"),
        route: mediaRoute.value,
        testId: "media-product-menu",
        tooltip: getLocaleText("monitoring_product_name"),
      },
      {
        ariaLabel: getLocaleText("menu.social", {
          platformName: appConfig.value.platformName,
        }),
        hasTeaser: socialProductTeaserEnabled.value,
        hasViewedTeaser: !viewedFeatureTeaser("has_social_platform"),
        icon: "social-streams",
        isActive: currentRouteProductMenu.value === "social",
        isEnabled: socialProductMenuEnabled.value,
        isVisible:
          socialProductMenuEnabled.value || socialProductTeaserEnabled.value,
        key: "social",
        label: getLocaleText("menu.social"),
        route: socialRoute.value,
        testId: "social-product-menu",
        tooltip: getLocaleText("social_product_name", {
          platformName: appConfig.value.platformName,
        }),
      },
      {
        ariaLabel: getLocaleText("insights_product_name"),
        hasTeaser: false,
        icon: "insights",
        isActive: currentRouteProductMenu.value === "insights",
        isEnabled: true,
        isVisible: insightsProductMenuEnabled.value,
        key: "insights",
        label: getLocaleText("insights_product_name"),
        route: insightsRoute.value,
        testId: "insights-product-menu",
        tooltip: getLocaleText("menu.insights"),
      },
      {
        ariaLabel: getLocaleText("menu.csl", {
          platformName: appConfig.value.platformName,
        }),
        hasTeaser: false,
        icon: "csl",
        isActive: currentRouteProductMenu.value === "csl",
        isEnabled: true,
        isVisible: cslProductMenuEnabled.value,
        key: "csl",
        label: getLocaleText("csl_product_name", {
          platformName: appConfig.value.platformName,
        }),
        route: cslRoute.value,
        testId: "csl-product-menu",
        tooltip: getLocaleText("menu.csl"),
      },
      {
        ariaLabel: getLocaleText("menu.outreach"),
        hasTeaser: false,
        icon: "outreach",
        isActive: currentRouteProductMenu.value === "outreach",
        isEnabled: outreachProductMenuEnabled.value,
        isVisible:
          outreachProductMenuEnabled.value ||
          outreachProductTeaserEnabled.value,
        key: "outreach",
        label: getLocaleText("menu.outreach"),
        route: outreachRoute.value,
        testId: "outreach-product-menu",
        tooltip: getLocaleText("menu.outreach"),
      },
      {
        ariaLabel: getLocaleText("menu.crm"),
        hasTeaser: false,
        iconImg: appConfig.value.platformIcons.narratives,
        isActive: currentRouteProductMenu.value === "crm",
        isEnabled: true,
        isVisible: crmMenuEnabled.value && isDesktop === true,
        key: "crm",
        label: getLocaleText("menu.crm"),
        route: crmRoute.value,
        testId: "crm-product-menu",
        tooltip: getLocaleText("menu.crm"),
      },
    ];

    return items.filter((item) => item.isVisible);
  });

  function handleProductMenuRouterLink(key: string) {
    track("Changed Product Menu Tab", { tab: key });
  }

  function handleProductMenuButtonClick(key: string) {
    if (key !== "social") return;
    openTeaserModal("has_social_platform");
  }

  async function openTeaserModal(feature: FeatureKey): Promise<void> {
    ModalService.open("TeaserModal", {
      props: {
        feature,
      },
    });

    try {
      await createViewedItemForFeature(feature);
    } catch {
      // don't do anything
    }
  }

  return {
    canViewSearchButton,
    competitorRoute,
    crmMenuEnabled,
    crmRoute,
    cslProductMenuEnabled,
    cslRoute,
    currentRouteProductMenu,
    handleProductMenuButtonClick,
    handleProductMenuRouterLink,
    hasHelpMenu,
    hasLaunchpadBetaEnabled,
    insightsProductMenuEnabled,
    insightsRoute,
    isDarkMode,
    launchpadDashboardRoute,
    logoConfig,
    mediaProductMenuEnabled,
    mediaRoute,
    menuLogoAlt,
    outreachProductMenuEnabled,
    outreachProductTeaserEnabled,
    outreachRoute,
    productMenuItems,
    socialProductMenuEnabled,
    socialProductTeaserEnabled,
    socialRoute,
  };
}
